import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useRoutingServices } from "@ryde/redux";
import {
  errors,
  getIsValidStationLocation,
  PASSENGERS_MAP_ADDED_STATION_HIGHLIGHT_MS,
  passengersMapLastAddedStationIdAtom,
  passengersStationsMapDataAtom,
  warnAndToastException,
  warnAndToastSimpleResult,
  type ICoordinate,
  type IPassengersMapStationInfo,
  type IPassengerStationsMapInfo,
  type IStationDto,
  type IWarkDistanceAndTime,
} from "@ryde/shared";

export const usePassengerMapStations = () => {
  const { t } = useTranslation();
  const { getWalkingDistanceAndTime } = useRoutingServices();

  const setPassengersStationsMapDataAtom = useSetRecoilState(
    passengersStationsMapDataAtom,
  );
  const setPassengersMapLastAddedStationId = useSetRecoilState(
    passengersMapLastAddedStationIdAtom,
  );

  const [distanceAndTimeCacheRefByIds, setDistanceAndTimeCacheRefByIds] =
    useState<Map<string, IWarkDistanceAndTime>>(new Map());

  const distanceAndTimeCacheRef = useRef(
    new Map<string, IWarkDistanceAndTime>(),
  );

  const getOriginDestinationStationIdKey = useEvent(
    (originStationId: number, destStationId: number) => {
      return `${originStationId},${destStationId}`;
    },
  );

  const fetchWalkingDistanceAndTime = useEvent(
    async (
      origin: ICoordinate,
      destination: ICoordinate,
      stationsIds?: { originStationId: number; destStationId: number },
    ): Promise<IWarkDistanceAndTime | void> => {
      // CacheRef by origin/dest lat-lng (avoid duplicate api lat/lng origin and destination)
      const key = `${origin.lat},${origin.lng}|${destination.lat},${destination.lng}`;
      const cached = distanceAndTimeCacheRef.current.get(key);
      if (cached) {
        return cached;
      }

      try {
        const distanceAndTimeResult = await getWalkingDistanceAndTime({
          origin,
          destination,
        });

        if (!distanceAndTimeResult.success || !distanceAndTimeResult.data) {
          return warnAndToastSimpleResult(distanceAndTimeResult, t);
        }

        distanceAndTimeCacheRef.current.set(key, distanceAndTimeResult.data);

        // CacheRef by origin/dest ids (get user distance and time)
        if (stationsIds?.originStationId && stationsIds?.destStationId) {
          const stationsIdsKey = getOriginDestinationStationIdKey(
            stationsIds.originStationId,
            stationsIds.destStationId,
          );

          setDistanceAndTimeCacheRefByIds(prev => {
            const next = new Map(prev);
            next.set(stationsIdsKey, distanceAndTimeResult.data!);
            return next;
          });
        }

        return distanceAndTimeResult.data;
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.common.technicalProblemOccurred,
          translationFunction: t,
          logWarnMessage: "Error while run fetchWalkingDistanceAndTime method",
        });
      }
    },
  );

  const getAssignStationsWalkDistanceAndTime = useEvent(
    async (
      stationMapData: IPassengerStationsMapInfo,
    ): Promise<(void | IWarkDistanceAndTime)[]> => {
      const {
        id: originStationId,
        latitude: originLat,
        longitude: originLng,
      } = stationMapData.stationInfo;

      if (!originLat || !originLng) {
        return [];
      }

      return Promise.all(
        stationMapData.assignStations
          .filter(aS => aS.latitude && aS.longitude)
          .map(({ id: destStationId, latitude: desLat, longitude: desLng }) => {
            return fetchWalkingDistanceAndTime(
              { lat: originLat, lng: originLng },
              { lat: desLat, lng: desLng },
              { originStationId, destStationId },
            );
          }),
      );
    },
  );

  const getAddNewStationsWalkDistanceAndTime = useEvent(
    async (
      { originLat, originLng }: { originLat: number; originLng: number },
      stationInfo: IPassengersMapStationInfo,
    ): Promise<(void | IWarkDistanceAndTime)[]> => {
      const { latitude: desLat, longitude: desLng } = stationInfo ?? {};
      if (!originLat || !originLng || !desLat || !desLng) {
        return [];
      }

      const res = await fetchWalkingDistanceAndTime(
        { lat: originLat, lng: originLng },
        { lat: desLat, lng: desLng },
      );

      return [res as IWarkDistanceAndTime];
    },
  );

  const addNewStationMap = useEvent((stationInfo: IStationDto): void => {
    if (getIsValidStationLocation(stationInfo?.metadata?.locationInfo)) {
      const { id, name, metadata } = stationInfo ?? {};
      const { isOfficial = false, locationInfo } = metadata ?? {};
      setPassengersStationsMapDataAtom(prev => {
        const newStation: IPassengerStationsMapInfo = {
          assignPassengers: [],
          assignStations: [],
          stationInfo: {
            id,
            addressName: name ?? "",
            isHomeAddress: !isOfficial,
            isOfficial: isOfficial,
            fullAddress: locationInfo?.fullAddress ?? "",
            latitude: locationInfo?.latitude as number,
            longitude: locationInfo?.longitude as number,
          },
        };
        return [...prev, newStation];
      });
    }
  });

  /** Puts a just-created station on the map, highlighted for a few seconds so the eye can find it. */
  const showAddedStation = useEvent((station: IStationDto): void => {
    addNewStationMap(station);
    setPassengersMapLastAddedStationId(station.id);
    setTimeout(
      () => setPassengersMapLastAddedStationId(undefined),
      PASSENGERS_MAP_ADDED_STATION_HIGHLIGHT_MS,
    );
  });

  return {
    distanceAndTimeCacheRefByIds,
    addNewStationMap,
    showAddedStation,
    fetchWalkingDistanceAndTime,
    getAddNewStationsWalkDistanceAndTime,
    getAssignStationsWalkDistanceAndTime,
  };
};
